/**
 * Enemy nameplates, Aion style: the name over a thin red HP bar, your debuffs (plus anyone's CC)
 * as small real-icon chips above the name with the pandemic-refresh glow, the cast bar with its
 * spell icon, a gold bracket + brighter bar on the selected target, and a threat colour
 * (orange = loose on a non-tank, red pulse = on you).
 *
 * Positioned via Renderer.project. Per frame: no Set/array/closure allocation (seen-stamps,
 * pooled aura entries and chips), the position transform is rewritten only when the rounded
 * pixel changes, and every other write goes through write-if-changed caches. Screen-space, so
 * the plate body is scaled by --hk-scale in CSS.
 */
import type { ContentRegistry, DeepReadonly, UnitId, World } from '@keystone/sim';
import type { Renderer, RenderUnit } from '../render/types';
import { SCHOOL_COLOR } from '../render/palette';
import { el, setClass } from './dom';
import { hudNow } from '../game/prediction';
import { iconUrl } from './icons';
import { DEG, SCALE_X, auraTimeKey, barIndex, intText, sweepIndex, timerLabel } from './kit';
import { type PlateAura, collectPlateAuras } from './plateAuras';
import { FrameCastBar } from './frames/castBar';
import { type ThreatState, threatState } from './frames/model';
import { attachAbilityTooltip } from './tooltip/view';
import { type TooltipLive, auraTooltipModel } from './tooltip/hud';
import { LEVEL_CLASS, levelBadge } from '../world/levelBadge'; // +field

/** One debuff chip (pooled per plate). */
class PlateChip {
  readonly root: HTMLElement;
  private readonly img: HTMLImageElement;
  private readonly fallback: HTMLElement;
  private readonly sweep: HTMLElement;
  private readonly time: HTMLElement;
  private readonly stacks: HTMLElement;
  iid = '';
  private auraId = '';
  private sweepIdx = -1;
  private timeKey = -1;
  private stacksShown = -1;
  private cc = false;
  private refresh = false;
  private shown = true;

  constructor(parent: HTMLElement, tip: () => ReturnType<typeof auraTooltipModel>) {
    this.root = el('div', 'np-aura', parent);
    this.img = el('img', 'np-aura-img', this.root);
    this.img.alt = '';
    this.img.draggable = false;
    this.img.decoding = 'async';
    this.fallback = el('div', 'np-aura-fallback', this.root);
    this.sweep = el('div', 'np-aura-sweep', this.root);
    this.time = el('div', 'np-aura-time hk-num', this.root);
    this.stacks = el('div', 'np-aura-stacks hk-num', this.root);
    attachAbilityTooltip(this.root, tip);
  }

  set(a: PlateAura): void {
    this.iid = a.iid;
    if (a.auraId !== this.auraId) {
      this.auraId = a.auraId;
      const url = iconUrl(a.auraId, 64) ?? iconUrl(a.abilityId, 64);
      if (url) {
        this.img.src = url;
        this.img.hidden = false;
        this.fallback.hidden = true;
      } else {
        this.img.removeAttribute('src');
        this.img.hidden = true;
        this.fallback.hidden = false;
        this.fallback.textContent = a.label;
        this.fallback.style.setProperty('--aura-color', a.color || SCHOOL_COLOR.physical);
      }
    }
    const si = sweepIndex(a.elapsedFrac);
    if (si !== this.sweepIdx) {
      this.sweepIdx = si;
      this.sweep.style.setProperty('--p', DEG[si]!);
    }
    const tk = auraTimeKey(a.remainingMs);
    if (tk !== this.timeKey) {
      this.timeKey = tk;
      this.time.textContent = timerLabel(tk);
    }
    const st = a.stacks > 1 ? a.stacks : 0;
    if (st !== this.stacksShown) {
      this.stacksShown = st;
      this.stacks.textContent = st ? intText(st) : '';
    }
    if (a.cc !== this.cc) {
      this.cc = a.cc;
      this.root.classList.toggle('cc', a.cc);
    }
    if (a.refreshable !== this.refresh) {
      this.refresh = a.refreshable;
      this.root.classList.toggle('refresh', a.refreshable);
    }
    this.show(true);
  }

  show(on: boolean): void {
    if (on === this.shown) return;
    this.shown = on;
    this.root.classList.toggle('hk-hidden', !on);
    if (!on) this.iid = '';
  }
}

interface Plate {
  id: UnitId;
  root: HTMLElement;
  /** Inner box animated on target change (root's transform positions the plate, .np-scale scales it). */
  body: HTMLElement;
  auras: HTMLElement;
  chips: PlateChip[];
  name: HTMLElement;
  /** +field: "Lv" badge (con colour), and the key it was drawn for. */
  lvl: HTMLElement;
  lvlKey: number;
  fill: HTMLElement;
  cast: FrameCastBar;
  /** Last frame this plate was seen (no per-frame Set). */
  stamp: number;
  x: number;
  y: number;
  fillIdx: number;
  threat: ThreatState;
}

const MAX_DIST = 55;
const MAX_DIST_SQ = MAX_DIST * MAX_DIST;
/** +polish: open-world plate radius (yd) for mobs that are neither targeted nor fighting you. */
const FIELD_DIST = 25;
const FIELD_DIST_SQ = FIELD_DIST * FIELD_DIST;
/** Plate footprint (px) used for overlap stacking. */
const PLATE_W = 118;
const PLATE_H = 26;

export interface PlateSpot {
  ru: { id: UnitId };
  x: number;
  y: number;
}

/**
 * +polish: de-overlap plates. The target first, then nearest-on-screen (lowest) first; each later
 * plate that overlaps a placed one moves up by one plate height until it is clear (≤ 6 steps).
 */
export function stackPlates(spots: PlateSpot[], selectedId: UnitId | null): void {
  spots.sort((a, b) => (a.ru.id === selectedId ? -1 : b.ru.id === selectedId ? 1 : b.y - a.y));
  for (let i = 1; i < spots.length; i++) {
    const s = spots[i]!;
    for (let step = 0; step < 6; step++) {
      let hit = false;
      for (let j = 0; j < i; j++) {
        const o = spots[j]!;
        if (Math.abs(o.x - s.x) < PLATE_W && Math.abs(o.y - s.y) < PLATE_H) {
          s.y = o.y - PLATE_H;
          hit = true;
        }
      }
      if (!hit) break;
    }
  }
}
const THREAT_CLS: Readonly<Record<ThreatState, string>> = { none: '', tanked: '', aggro: 'aggro', loose: 'loose' };

export class Nameplates {
  readonly root: HTMLElement;
  private plates = new Map<UnitId, Plate>();
  /** Latest frame state for aura hover tooltips (mutated in place). */
  private readonly live: TooltipLive = { world: null as never, content: null as never, playerId: null };
  private lastSelected: UnitId | null = null;
  private frame = 0;
  private readonly pool: PlateAura[] = [];
  private readonly list: PlateAura[] = [];
  private hasTarget = false;

  constructor(parent: HTMLElement, private readonly onClick: (id: UnitId) => void) {
    this.root = el('div', 'nameplates np-layer', parent);
  }

  private readonly cand: { ru: RenderUnit; x: number; y: number }[] = [];

  update(
    world: DeepReadonly<World>,
    content: ContentRegistry,
    units: readonly RenderUnit[],
    renderer: Renderer,
    focus: { x: number; y: number },
    selectedId: UnitId | null,
    playerId: UnitId | null,
    /** +castux: out of range of the main ability / out of sight → the plate greys out. */
    dimFor?: (id: UnitId) => boolean,
  ): void {
    const stamp = ++this.frame;
    this.live.world = world;
    this.live.content = content;
    this.live.playerId = playerId;
    const heroLevel = playerId ? world.units[playerId]?.level : undefined; // +field
    // +polish: in the open world only nearby mobs (≤ FIELD_DIST), the target and mobs fighting
    // the party get plates; overlapping plates then stack upward (nearest keeps its spot).
    const field = heroLevel !== undefined;
    const cand = this.cand;
    cand.length = 0;
    for (let i = 0; i < units.length; i++) {
      const ru = units[i]!;
      if (ru.faction !== 'enemy' || !ru.alive) continue;
      const dx = ru.pos.x - focus.x;
      const dy = ru.pos.y - focus.y;
      const d2 = dx * dx + dy * dy;
      if (d2 > MAX_DIST_SQ) continue;
      if (field && d2 > FIELD_DIST_SQ && ru.id !== selectedId && threatState(world.units[ru.id], world, playerId) === 'none') continue;
      const sp = renderer.project(ru.pos, renderer.unitHeight(ru.id) + 0.6);
      if (!sp.visible) continue;
      cand.push({ ru, x: sp.x, y: sp.y });
    }
    stackPlates(cand, selectedId);
    for (const c of cand) {
      const ru = c.ru;
      const p = this.plates.get(ru.id) ?? this.create(ru);
      p.stamp = stamp;
      const u = world.units[ru.id];
      const x = Math.round(c.x);
      const y = Math.round(c.y);
      if (x !== p.x || y !== p.y) {
        p.x = x;
        p.y = y;
        p.root.style.transform = `translate3d(${x}px,${y}px,0)`;
      }
      const fi = barIndex(ru.hpFrac);
      if (fi !== p.fillIdx) {
        p.fillIdx = fi;
        p.fill.style.transform = SCALE_X[fi]!;
      }
      setClass(p.root, 'selected', ru.id === selectedId);
      const th = threatState(u, world, playerId);
      if (th !== p.threat) {
        if (THREAT_CLS[p.threat]) p.root.classList.remove(THREAT_CLS[p.threat]);
        if (THREAT_CLS[th]) p.root.classList.add(THREAT_CLS[th]);
        p.threat = th;
      }
      if (u) this.drawLevel(p, u.level, heroLevel, !!u.elite); // +field
      p.cast.update(u, world, content);
      setClass(p.root, 'casting', p.cast.casting);
      if (dimFor) setClass(p.root, 'oor', dimFor(ru.id)); // +castux
      this.updateAuras(p, u, world, content, playerId);
    }
    for (const [id, p] of this.plates) {
      if (p.stamp !== stamp) {
        p.root.remove();
        this.plates.delete(id);
      }
    }
    const has = !!selectedId && this.plates.has(selectedId);
    if (has !== this.hasTarget) {
      this.hasTarget = has;
      setClass(this.root, 'has-target', has);
    }
    if (selectedId !== this.lastSelected) {
      this.lastSelected = selectedId;
      // "Target changed" pop on the new plate (Web Animations: compositor-only, no reflow).
      const p = selectedId ? this.plates.get(selectedId) : undefined;
      p?.body.animate?.([{ transform: 'scale(1.18)', filter: 'brightness(1.8)' }, { transform: 'scale(1)', filter: 'brightness(1)' }], { duration: 190, easing: 'cubic-bezier(.2,.8,.2,1)' });
    }
  }

  /** +field: the level badge, redrawn only when the mob's or the hero's level changes. */
  private drawLevel(p: Plate, mob: number | undefined, hero: number | undefined, elite: boolean): void {
    const key = mob === undefined ? -1 : mob * 1000 + (hero ?? 0) * 2 + (elite ? 1 : 0);
    if (key === p.lvlKey) return;
    p.lvlKey = key;
    const b = levelBadge(mob, hero, elite);
    p.lvl.hidden = !b;
    if (!b) return;
    p.lvl.textContent = b.text;
    p.lvl.className = `np-level ${LEVEL_CLASS[b.color]}${b.elite ? ' elite' : ''}`;
    p.lvl.title = `Level ${b.text}${b.elite ? ' elite' : ''}`;
    setClass(p.root, 'elite-mob', b.elite);
  }

  private updateAuras(p: Plate, u: DeepReadonly<World>['units'][string] | undefined, world: DeepReadonly<World>, content: ContentRegistry, playerId: UnitId | null): void {
    const n = u ? collectPlateAuras(u.auras, playerId, hudNow(world), content, this.pool, this.list) : 0;
    for (let i = 0; i < n; i++) this.chip(p, i).set(this.list[i]!);
    for (let i = n; i < p.chips.length; i++) p.chips[i]!.show(false);
    this.list.length = 0;
  }

  private chip(p: Plate, i: number): PlateChip {
    let c = p.chips[i];
    if (!c) {
      const box: { chip: PlateChip | null } = { chip: null };
      const id = p.id;
      c = new PlateChip(p.auras, () => auraTooltipModel(this.live.world ? this.live : null, id, box.chip?.iid || null));
      box.chip = c;
      p.chips.push(c);
    }
    return c;
  }

  private create(ru: RenderUnit): Plate {
    const id = ru.id;
    const root = el('div', 'np', this.root);
    const scaler = el('div', 'np-scale', root);
    const body = el('div', 'np-body', scaler);
    const auras = el('div', 'np-auras', body);
    const name = el('div', 'np-name', body);
    const lvl = el('span', 'np-level', name); // +field
    lvl.hidden = true;
    name.appendChild(document.createTextNode(ru.name));
    const bar = el('div', 'np-bar', body);
    const fill = el('div', 'np-fill', bar);
    el('div', 'np-gloss', bar);
    const cast = new FrameCastBar(body, { className: 'np-cast', showTarget: false });
    if (ru.tier === 'boss' || ru.tier === 'miniboss') root.classList.add('boss');
    else if (ru.tier === 'big') root.classList.add('elite');
    else if (ru.tier === 'fodder') root.classList.add('minor');
    root.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 && e.button !== 2) return;
      e.stopPropagation();
      this.onClick(id);
    });
    const p: Plate = { id, root, body, auras, chips: [], name, lvl, lvlKey: -2, fill, cast, stamp: 0, x: NaN, y: NaN, fillIdx: -1, threat: 'none' };
    this.plates.set(id, p);
    return p;
  }
}
